import type { RouteLocationNormalized } from "vue-router";

/**
 * Fallback capture of the ad attribution parameters on the landing URL.
 *
 * The cookie the marketing site writes on `.notary.io` is the primary path, and it
 * reaches the API on its own. This is only the backup for when that cookie is
 * missing, and it exists because the URL cannot survive the funnel: the register
 * store forwards only a short allowlist between steps, and account creation is
 * several navigations away from the landing page.
 *
 * Kept in `localStorage` under its own key rather than in the register store,
 * which `pages/login.vue` clears on every mount.
 */

export const ATTRIBUTION_STORAGE_KEY = "nio_attr_fallback";

/** The parameters the marketing site forwards. Must match the API's allowlist. */
export const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
] as const;

export type AttributionSnapshot = {
  params: Record<string, string>;
  landing?: string;
  referrer?: string;
  ts?: string;
};

/**
 * Store the attribution parameters from this route, if any. First write wins: a
 * later navigation without parameters must not erase the campaign that brought the
 * visitor in.
 */
export function captureAdAttribution(route: RouteLocationNormalized): void {
  try {
    const params: Record<string, string> = {};

    for (const key of ATTRIBUTION_PARAM_KEYS) {
      const value = route.query?.[key];
      const text = Array.isArray(value) ? value[0] : value;

      if (typeof text === "string" && text.trim() !== "") {
        params[key] = text.trim();
      }
    }

    if (Object.keys(params).length === 0) return;
    if (localStorage.getItem(ATTRIBUTION_STORAGE_KEY)) return;

    const snapshot: AttributionSnapshot = {
      params,
      landing: route.fullPath,
      referrer: document.referrer || undefined,
      ts: new Date().toISOString(),
    };

    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // A blocked or full localStorage costs a row of reporting, never a signup.
  }
}

/**
 * The stored snapshot, or undefined. Shaped to be sent straight through as the
 * API's `attribution_fallback` body parameter, which the backend reads only when
 * the cookie is absent.
 */
export function readAdAttribution(): AttributionSnapshot | undefined {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" && parsed.params ? parsed : undefined;
  } catch {
    return undefined;
  }
}
